import { BaseProvider } from "./base.provider";

export interface BadgeDto {
  badge_id: string;
  name: string;
  img_url: string;
  variant_name: string;
  filter_img_url: string | null;
  is_visible: boolean;
  available_from: string | null;
  available_to: string | null;
  action_type: string;
  action_threshold: number;
  action_description: string;
}

export interface PaginatedBadges {
  data: BadgeDto[];
  page: number;
  page_size: number;
  has_next: boolean;
}

export interface CreateBadgePayload {
  name: string;
  badge_image_id?: string;
  img_content?: string;
  is_visible?: boolean;
  available_from?: string | null;
  available_to?: string | null;
  action_type: string;
  action_threshold: number;
  action_description: string;
}

export interface CreateBadgeResult {
  badge_id: string;
  img_url?: string;
}

export interface UpdateBadgePayload {
  is_visible?: boolean;
  available_from?: string | null;
  available_to?: string | null;
  action_threshold: number;
  action_description: string;
}

export interface UserBadgeGrant {
  badge_id: string;
  name: string;
  img_url: string;
  variant_id: string;
  variant_name: string;
  filter_img_url: string | null;
  granted_at: string;
}

export interface BadgeVariantType {
  name: string;
  filter_img_url: string | null;
}

export interface BadgeImageDto {
  badge_image_id: string;
  name: string;
  img_url: string;
}

export interface PaginatedBadgeImages {
  data: BadgeImageDto[];
  page: number;
  page_size: number;
  has_next: boolean;
}

export interface PresignedUpload {
  upload_url: string;
  form_fields: Record<string, string>;
}

export interface CreateImageResult extends PresignedUpload {
  badge_image_id: string;
}

export interface BadgeVariant {
  variant_id: string;
  badge_id: string;
  variant_name: string;
  variant_description: string | null;
  filter_img_url: string | null;
}

export interface AddBadgeVariantPayload {
  variant_name: string;
  variant_description?: string;
}

class BadgesProvider extends BaseProvider {
  listAll(
    page = 1,
    filter?: "event" | "static",
  ): Promise<PaginatedBadges> {
    const params = new URLSearchParams({ page: String(page) });
    if (filter === "event") params.set("only_event_badges", "true");
    else if (filter === "static") params.set("only_static_badges", "true");
    return this.request<PaginatedBadges>(`/v1/admin/badges?${params}`);
  }

  create(payload: CreateBadgePayload): Promise<CreateBadgeResult> {
    return this.request<CreateBadgeResult>("/v1/admin/badges", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  update(badgeId: string, payload: UpdateBadgePayload): Promise<void> {
    return this.request<void>(
      `/v1/admin/badges/${encodeURIComponent(badgeId)}`,
      {
        method: "PUT",
        body: JSON.stringify(payload),
      },
    );
  }

  uploadImage(badgeId: string, base64Content: string): Promise<void> {
    return this.request<void>(
      `/v1/admin/badges/${encodeURIComponent(badgeId)}/image`,
      {
        method: "POST",
        body: JSON.stringify({ content: base64Content }),
      },
    );
  }

  delete(badgeId: string): Promise<void> {
    return this.request<void>(
      `/v1/admin/badges/${encodeURIComponent(badgeId)}`,
      {
        method: "DELETE",
      },
    );
  }

  getForUser(userId: string): Promise<UserBadgeGrant[]> {
    return this.request<UserBadgeGrant[]>(
      `/v1/users/${encodeURIComponent(userId)}/badges`,
    );
  }

  listImages(page = 1, search?: string): Promise<PaginatedBadgeImages> {
    const params = new URLSearchParams({ page: String(page) });
    if (search) params.set("search", search);
    return this.request<PaginatedBadgeImages>(
      `/v1/admin/badges/images?${params}`,
    );
  }

  createImage(name: string): Promise<CreateImageResult> {
    return this.request<CreateImageResult>("/v1/admin/badges/images", {
      method: "POST",
      body: JSON.stringify({ name }),
    });
  }

  getImageUploadUrl(badgeImageId: string): Promise<PresignedUpload> {
    return this.request<PresignedUpload>(
      `/v1/admin/badges/images/${encodeURIComponent(badgeImageId)}/image`,
      { method: "POST" },
    );
  }

  async uploadToPresignedUrl(
    presigned: PresignedUpload,
    file: File,
  ): Promise<void> {
    const form = new FormData();
    for (const [key, value] of Object.entries(presigned.form_fields)) {
      form.append(key, value);
    }
    form.append("Content-Type", file.type);
    form.append("file", file);

    const res = await fetch(presigned.upload_url, {
      method: "POST",
      body: form,
    });

    if (!res.ok) {
      const text = await res.text().catch(() => "Unknown error");
      throw new Error(text || `Upload failed: HTTP ${res.status}`);
    }
  }

  updateImageName(badgeImageId: string, name: string): Promise<BadgeImageDto> {
    return this.request<BadgeImageDto>(
      `/v1/admin/badges/images/${encodeURIComponent(badgeImageId)}`,
      {
        method: "PUT",
        body: JSON.stringify({ name }),
      },
    );
  }

  getImage(badgeImageId: string): Promise<BadgeImageDto> {
    return this.request<BadgeImageDto>(
      `/v1/admin/badges/images/${encodeURIComponent(badgeImageId)}`,
    );
  }

  deleteImage(badgeImageId: string): Promise<void> {
    return this.request<void>(
      `/v1/admin/badges/images/${encodeURIComponent(badgeImageId)}`,
      { method: "DELETE" },
    );
  }

  listVariantTypes(): Promise<BadgeVariantType[]> {
    return this.request<BadgeVariantType[]>("/v1/admin/badges/variant-types");
  }

  createVariantType(name: string, filterContent?: string): Promise<void> {
    return this.request<void>("/v1/admin/badges/variant-types", {
      method: "POST",
      body: JSON.stringify({ name, filter_content: filterContent }),
    });
  }

  uploadVariantTypeImage(
    variantName: string,
    base64Content: string,
  ): Promise<void> {
    return this.request<void>(
      `/v1/admin/badges/variant-types/${encodeURIComponent(variantName)}/image`,
      {
        method: "POST",
        body: JSON.stringify({ content: base64Content }),
      },
    );
  }

  updateBadgeVariant(
    badgeId: string,
    variantId: string,
    variantDescription: string | null,
  ): Promise<void> {
    return this.request<void>(
      `/v1/admin/badges/${encodeURIComponent(badgeId)}/variants/${encodeURIComponent(variantId)}`,
      {
        method: "PATCH",
        body: JSON.stringify({ variant_description: variantDescription }),
      },
    );
  }

  getVariantsForBadge(badgeId: string): Promise<BadgeVariant[]> {
    return this.request<BadgeVariant[]>(
      `/v1/admin/badges/${encodeURIComponent(badgeId)}/variants`,
    );
  }

  addVariantToBadge(
    badgeId: string,
    payload: AddBadgeVariantPayload,
  ): Promise<{ variant_id: string }> {
    return this.request<{ variant_id: string }>(
      `/v1/admin/badges/${encodeURIComponent(badgeId)}/variants`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );
  }

  deleteVariantFromBadge(badgeId: string, variantId: string): Promise<void> {
    return this.request<void>(
      `/v1/admin/badges/${encodeURIComponent(badgeId)}/variants/${encodeURIComponent(variantId)}`,
      { method: "DELETE" },
    );
  }

  grantToUser(badgeId: string, userId: string): Promise<void> {
    return this.request<void>(
      `/v1/admin/badges/${encodeURIComponent(badgeId)}/grant/${encodeURIComponent(userId)}`,
      { method: "POST" },
    );
  }

  revokeFromUser(badgeId: string, userId: string): Promise<void> {
    return this.request<void>(
      `/v1/admin/badges/${encodeURIComponent(badgeId)}/revoke/${encodeURIComponent(userId)}`,
      { method: "DELETE" },
    );
  }
}

export const badgesProvider = new BadgesProvider();
